/**
 * CreateCircle — Create or Edit a Circle.
 * Routes: /circles/create  (new)
 *         /circles/:circleId/edit  (edit — admin only)
 *
 * Form: name (3-60), description (10-500), visibility, categoryTags (max 5),
 *       location (optional). Admin can also archive.
 */

import React, { useEffect, useState } from 'react';
import { Globe, Lock, Archive, ChevronLeft } from 'react-feather';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import { useCircleId } from '../hooks/useCircleId';
import { slugify } from '../utils/circleSlug';
import GlassPanel from '../components/GlassPanel';
import { createCircle, getCircleDetail, updateCircle, archiveCircle } from '../services/circleService';
import '../styles/modern.css';

const MAX_TAGS = 5;
const TAG_MIN = 2;
const TAG_MAX = 30;

export default function CreateCircle() {
  const { circleSlug } = useParams<{ circleSlug?: string }>();
  const { circleId } = useCircleId(circleSlug);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();

  const isEdit = Boolean(circleSlug);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  // ─── Auth gate ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      redirectToLogin();
    }
  }, [isAuthenticated, loading, redirectToLogin]);

  // ─── Load existing circle for edit ────────────────────────────────────
  useEffect(() => {
    if (!circleId || !isAuthenticated) return;
    (async () => {
      setLoading(true);
      try {
        const data = await getCircleDetail(circleId);
        if (data.membership?.role !== 'admin') {
          setError('Only admins can edit this Circle.');
          setLoading(false);
          return;
        }
        const c = data.circle;
        setName(c.name);
        setDescription(c.description || '');
        setVisibility(c.visibility);
        setTags(c.categoryTags || []);
        setLocation(c.location || '');
      } catch (err: any) {
        setError(err?.message || 'Circle not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [circleId, isAuthenticated]);

  // ─── Tag handling ─────────────────────────────────────────────────────
  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || t.length < TAG_MIN || t.length > TAG_MAX) return;
    if (tags.length >= MAX_TAGS) return;
    if (tags.includes(t)) return;
    setTags([...tags, t]);
    setTagInput('');
  };

  const removeTag = (idx: number) => {
    setTags(tags.filter((_, i) => i !== idx));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  // ─── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (name.length < 3 || name.length > 60) {
      setError('Name must be 3-60 characters');
      return;
    }
    if (description.length < 10 || description.length > 500) {
      setError('Description must be 10-500 characters');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && circleId) {
        await updateCircle(circleId, {
          name,
          description,
          visibility,
          categoryTags: tags,
          location: location || undefined,
        });
        navigate(`/circles/${circleSlug}`, { state: { circleId } });
      } else {
        const result = await createCircle({
          name,
          description,
          visibility,
          categoryTags: tags,
          location: location || undefined,
        });
        navigate(`/circles/${slugify(name)}`, { state: { circleId: result.circleId } });
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ─── Archive ──────────────────────────────────────────────────────────
  const handleArchive = async () => {
    if (!circleId || !confirm('Archive this Circle? This will hide it from discovery and prevent new members.')) return;
    setSaving(true);
    try {
      await archiveCircle(circleId);
      navigate('/circles');
    } catch (err: any) {
      setError(err?.message || 'Failed to archive');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  if (!isAuthenticated) return null;

  return (
    <div className="page-shell">
      <div className="page-shell__inner" style={{ maxWidth: 720 }}>
        {/* Back */}
        <div style={{ marginBottom: 20 }}>
          <button
            onClick={() => navigate(isEdit ? `/circles/${circleSlug}` : '/circles', isEdit ? { state: { circleId } } : undefined)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 999, padding: '7px 14px', color: '#fff', fontSize: 13,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <ChevronLeft size={14} /> {isEdit ? 'back' : 'circles'}
          </button>
        </div>

        {loading && (
          <GlassPanel hover={false}>
            <div className="skel-line wide pulse" style={{ height: 18, marginBottom: 10 }} />
            <div className="skel-line medium pulse" style={{ height: 12 }} />
          </GlassPanel>
        )}

        {!loading && (
          <GlassPanel hover={false}>
            <form onSubmit={handleSubmit}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 20px', letterSpacing: '-0.025em' }}>
                {isEdit ? 'edit circle' : 'create a circle'}
              </h2>

              {error && <div className="circle-message-banner error" style={{ marginBottom: 16 }}>{error}</div>}

              {/* Name */}
              <div className="circle-form-label">
                name
                <input
                  type="text"
                  className="circle-form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                  placeholder="Circle name (3-60 chars)"
                  required
                />
                <span className="circle-form-hint">{name.length}/60</span>
              </div>

              {/* Description */}
              <div className="circle-form-label">
                description
                <textarea
                  className="circle-form-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                  placeholder="What is this Circle about? (10-500 chars)"
                  rows={4}
                  required
                />
                <span className="circle-form-hint">{description.length}/500</span>
              </div>

              {/* Visibility */}
              <div className="circle-form-label">
                visibility
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    type="button"
                    className={visibility === 'public' ? 'primary-button' : undefined}
                    onClick={() => setVisibility('public')}
                    style={visibility === 'public' ? { fontSize: 12 } : {
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10, padding: '7px 14px', color: '#fff', fontSize: 12,
                      fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <Globe size={14} /> public
                  </button>
                  <button
                    type="button"
                    className={visibility === 'private' ? 'primary-button' : undefined}
                    onClick={() => setVisibility('private')}
                    style={visibility === 'private' ? { fontSize: 12 } : {
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10, padding: '7px 14px', color: '#fff', fontSize: 12,
                      fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <Lock size={14} /> private
                  </button>
                </div>
              </div>

              {/* Tags */}
              <div className="circle-form-label">
                category tags ({tags.length}/{MAX_TAGS})
                <div className="circle-form-tags-input">
                  {tags.map((t, i) => (
                    <span key={t} className="tag-pill tag-pill--sm tag-pill--violet" style={{ gap: 4 }}>
                      {t}
                      <button
                        type="button"
                        onClick={() => removeTag(i)}
                        style={{
                          background: 'none', border: 'none', color: 'inherit',
                          cursor: 'pointer', fontSize: 14, opacity: 0.7, padding: 0,
                        }}
                      >
                        x
                      </button>
                    </span>
                  ))}
                  {tags.length < MAX_TAGS && (
                    <input
                      type="text"
                      className="circle-form-tag-field"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      onBlur={addTag}
                      placeholder="Type a tag and press Enter"
                      maxLength={TAG_MAX}
                    />
                  )}
                </div>
              </div>

              {/* Location */}
              <div className="circle-form-label">
                location (optional)
                <input
                  type="text"
                  className="circle-form-input"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="City, country, or 'Everywhere'"
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button
                  type="button"
                  onClick={() => navigate(isEdit ? `/circles/${circleSlug}` : '/circles', isEdit ? { state: { circleId } } : undefined)}
                  style={{
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12, padding: '10px 18px', color: '#fff', fontSize: 13.5,
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  cancel
                </button>
                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? 'saving...' : isEdit ? 'save changes' : 'create circle'}
                </button>
              </div>

              {/* Archive (edit only) */}
              {isEdit && (
                <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <button
                    type="button"
                    onClick={handleArchive}
                    disabled={saving}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: 'none', border: '1px solid rgba(255,107,107,0.2)',
                      borderRadius: 8, padding: '7px 14px', color: '#ff6b6b', fontSize: 13,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <Archive size={14} /> archive circle
                  </button>
                </div>
              )}
            </form>
          </GlassPanel>
        )}
      </div>
    </div>
  );
}
